/**
 * The hand-in on a Drive brand (2026-11-02): the client's own pipeline picks the
 * videos up from Drive, so the editor pastes the Drive link here and nothing opens
 * FreeFrame. The server checks the link opens without a login and holds a video
 * (`drive-link.ts`); a good link IS the hand-in. Shared by the hub, the dashboard
 * tile and the card page, each passing its own button classes.
 *
 * @module web/screens/DriveHandin
 */
import { useState, type FormEvent } from "react";
import { ApiError, post } from "../api/client.ts";
import { useI18n, type MessageKey } from "../i18n/index.tsx";

const DRIVE_ERRORS: Record<string, MessageKey> = {
  drive_link_required: "dashboard.driveErrInvalid",
  drive_link_invalid: "dashboard.driveErrInvalid",
  drive_link_not_public: "dashboard.driveErrNotPublic",
  drive_no_video: "dashboard.driveErrNoVideo",
  drive_link_same: "dashboard.driveErrSame",
  drive_check_unavailable: "dashboard.driveErrUnavailable",
  stage_changed: "dashboard.submitErrStageChanged",
  revision_open: "dashboard.submitErrRevisionOpen",
};

export function DriveHandin({
  workItemId,
  title,
  onDone,
  onCancel,
  primaryClass,
  buttonClass,
}: {
  workItemId: string;
  title: string | null;
  onDone: () => void;
  /** Absent on a revision, where the form is the only way forward. */
  onCancel?: () => void;
  primaryClass: string;
  buttonClass: string;
}) {
  const { t } = useI18n();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);

  async function handIn(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!link.trim()) {
      setError("dashboard.driveErrInvalid");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await post(`/api/editor/work/${workItemId}/submit`, { driveUrl: link.trim() });
      onDone();
    } catch (err) {
      setError((err instanceof ApiError && DRIVE_ERRORS[err.message]) || "dashboard.submitErrGeneric");
      setBusy(false);
    }
  }

  return (
    <form className="drive-handin" data-drive-handin onSubmit={(e) => void handIn(e)} noValidate>
      <p className="drive-handin-title">{t("dashboard.driveHeading", { title: title ?? "" })}</p>
      <p className="drive-handin-line">{t("dashboard.driveLine")}</p>
      <input
        className="drive-handin-input"
        data-drive-link
        type="url"
        inputMode="url"
        autoComplete="off"
        value={link}
        disabled={busy}
        placeholder={t("dashboard.drivePlaceholder")}
        aria-label={t("dashboard.drivePlaceholder")}
        onChange={(e) => setLink(e.currentTarget.value)}
      />
      <p className="drive-handin-hint">{t("dashboard.driveHint")}</p>
      {error && <p className="editor-offer-error" role="alert">{t(error)}</p>}
      <div className="drive-handin-actions">
        <button className={primaryClass} type="submit" disabled={busy}>
          {busy ? t("dashboard.driveChecking") : t("dashboard.driveHandIn")}
        </button>
        {onCancel && (
          <button className={buttonClass} type="button" disabled={busy} onClick={onCancel}>{t("dashboard.driveCancel")}</button>
        )}
      </div>
    </form>
  );
}
